<template>
  <div>
    <v-card class="pa-4 elevation-5">
      <v-row 
        class="mt-n4"
        no-gutters
      >
        <v-col 
          cols="6"
          class="pr-4"
        >
          <v-autocomplete
            v-model="selectedLetterType"
            v-validate="'required'"
            label="Letter Type"
            item-text="name"
            item-value="id"
            :items="letterTypes"
            hide-selected
            :error-messages="errors.collect('selectedLetterType')"
            data-vv-name="selectedLetterType"  
            data-vv-as="letter type"
          />
        </v-col>
        <v-col
          cols="6"
        >
          <v-menu
            v-model="letterDateMenu"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                v-model="letterDateDisplay"
                v-validate="'required'"
                label="Letter Date"
                :error-messages="errors.collect('letterDateDisplay')"
                data-vv-name="letterDateDisplay"  
                data-vv-as="letter date"    
                readonly
                class="label-position"
                v-on="on"
              >
                <template v-slot:prepend-inner>
                  <v-icon>
                    mdi-calendar
                  </v-icon>
                </template>
              </v-text-field>
            </template>
            <v-date-picker
              v-model="letterDate"
              no-title
              @input="letterDateMenu = false"
            />
          </v-menu>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="8">
          <v-data-table
            v-model="selectedEmployees"
            v-validate="'required'"
            dense
            :loading="tableLoading"
            :headers="headers"
            :items="filteredEmployees"
            item-key="employee_uid"
            hide-default-footer
            fixed-header
            disable-pagination
            show-select
            class="elevation-1 mb-3"
            height="420px"
            :error-messages="errors.collect('selectedEmployees')"
            data-vv-name="selectedEmployees"
            data-vv-as="employee selection"
            return-object
          >
            <template v-slot:top>
              <v-toolbar
                flat
                color="white"
              >
                <v-toolbar-title>Employee Selection:</v-toolbar-title>
              </v-toolbar>
            </template>
            <template v-slot:item.company_id="{ item }">
              <v-select
                v-model="item.company_id"
                :items="item.companyOptions"
                item-text="name"
                item-value="id"
                dense
                hide-details
              />
            </template>
          </v-data-table>
          <div :class="errors.first('selectedEmployees') ? 'py-0' : 'py-3'">
            <span class="red--text text--lighten-1 caption">{{ errors.first('selectedEmployees') }}</span>
          </div>
        </v-col>

        <v-col cols="4">
          <v-text-field
            v-model="searchInput"
            placeholder="Filter employee by name"
            append-icon="search"
            label="Search Employee"
            hide-no-data
            hide-selected
          />
          
          <v-autocomplete
            v-model="filters.costCenters"
            :items="costCenters"
            item-text="name"
            item-value="id"
            value="id"
            label="Cost Center"
            placeholder="Filter employee by cost center"
            clearable
            hide-selected
            multiple
            small-chips
            deletable-chips
            chips
          />

          <v-autocomplete
            v-model="filters.departments"
            :items="departments"
            item-text="name"
            item-value="id"
            value="id"
            label="Department"
            placeholder="Filter employee by department"
            clearable
            hide-selected
            multiple
            deletable-chips
            small-chips
            chips
          />

          <v-autocomplete
            v-model="filters.positions"
            :items="positions"
            item-text="label"
            item-value="id"
            value="id"
            label="Position"
            placeholder="Filter employee by position"
            clearable
            hide-selected
            multiple
            deletable-chips
            chips
            small-chips
          />

          <v-autocomplete
            v-model="filters.companies"
            :items="exactCompanies"
            item-text="name"
            item-value="id"
            value="id"
            label="Companies"
            placeholder="Filter employee by company"
            clearable
            hide-selected
            multiple
            deletable-chips
            chips
            small-chips
          />
        </v-col>
      </v-row>

      <v-card-actions style="right: 0px;">
        <v-spacer />
        <v-btn
          :loading="progressing"
          :disabled="employees.length < 1"
          color="hrissecondary"
          class="white--text px-10"
          @click="submitForm"
        >
          Release Letter
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      companies: {
        type: Array,
        required: true,
      },
      letterTypes: {
        type: Array,
        required: true,
      },
    },
    data () {
      return {
        dialog: false,
        loading: false,
        progressing: false,
        positions: [],
        employees: [],
        departments: [],
        costCenters: [],
        tableLoading: false,
        searchInput: '',
        filters: {
          costCenters: '',
          departments: '',
          positions: '',
          companies: '',
        },
        headers: [
          {
            text: 'Name',
            align: 'left',
            sortable: true,
            value: 'person.name',
          },
          {
            text: 'Position',
            align: 'left',
            sortable: true,
            value: 'current_position_among_many.position.name',
          },
          {
            text: 'Company',
            align: 'left',
            sortable: true,
            value: 'company_id',
          },
        ],
        selectedEmployees: [],
        exactCompanies: [],
        letterDateMenu: false,
        letterDate: null,
        selectedLetterType: null,
      }
    },
    computed: {
      letterDateDisplay () {
        return this.letterDate ? this.$moment(this.letterDate, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
      filteredEmployees () {
        let _this = this
        _this.searchInput = _this.searchInput.trim()
        let primaryFilterEmployees = this.employees.filter(function (employee) {
          return ((employee.person || {}).name || '').toLowerCase().indexOf(_this.searchInput.toLowerCase()) !== -1
        })
        let filteredPositions = this.customFilterPositions(primaryFilterEmployees)
        let filteredCompanies = this.customFilterCompanies(filteredPositions)
        let filteredDepartments = this.customFilterDepartments(filteredCompanies)
        let filteredCostCenters = this.customFilterCostCenters(filteredDepartments)
        
        return filteredCostCenters
      },
    },
    watch: {
      filteredEmployees (newList) {
        this.$validator.pause()
        
        const newCompEmpUids = new Set(newList.map(compEmp => compEmp.company_employee_uid))

        this.selectedEmployees = this.selectedEmployees.filter(selectedEmp =>
          newCompEmpUids.has(selectedEmp.company_employee_uid)
        )

        this.$nextTick(() => {
          this.$validator.resume()
        })
      },
    },
    created () {
      this.exactCompanies = this.companies.filter(c => c.id !== 'ALL')
      this.loadData()
    },
    methods: {
      loadData () {
        this.tableLoading = true 

        Promise.all([
          this.$api.positions().list(),
          this.$api.hrisEmployeePositions().listEmployees(),
          this.$api.hrisDepartments().list(['name'], false, true),
          this.$api.costCenters().listAll(),
        ]).then(([position, employee, departments, costCenters]) => {
          this.employees = _.orderBy(
            employee.data.map(emp => {
              const companyOptions = emp.company_employees.map(ce => ({
                id: ce.company.id,
                name: ce.company.name,
                company_employee_uid: ce.company_employee_uid,
              }))

              return {
                ...emp,
                companyOptions,
                company_id: companyOptions.length ? companyOptions[0].id : null,
                employee_uid: emp.employee_uid,
              }
            }),
            ['person.name'],
            ['asc']
          )

          this.positions = position.data.map(pos => ({
            id: pos.id,
            label: `${pos.name} (${pos.job_grade.job_group.name})`,
          }))
          this.departments = departments.data
          this.costCenters = _.orderBy(costCenters.data, ['name'], ['asc'])
          this.tableLoading = false
        })
      },
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.setProgressing(true)
            this.$api.hrisEmployeePositions().generateLetter(this.prepareFormData())
              .then(res => {
                this.loadData()
                this.$snack({ message: res.data.status })

                this.setProgressing(false)
                this.dialog = false
              })
              .catch((err) => {
                if (err.response.status === 422) {              
                  this.$snack({ 
                    color: 'error',
                    message: err.response.data.status, 
                  })
                }            
            
                this.setProgressing(false)
              })
          })
      },
      prepareFormData () {
        return {
          letter_type: this.selectedLetterType,
          letter_date: this.letterDate,
          employees: this.selectedEmployees.map(emp => {
            const selectedCompany = emp.companyOptions.find(
              c => c.id === emp.company_id
            )

            return {
              employee_uid: emp.employee_uid,
              company_id: emp.company_id,
              employee_position_id: emp.current_position_among_many.id,
              company_employee_uid: selectedCompany ? selectedCompany.company_employee_uid : null,
            }
          }),
        }
      },
      customFilterPositions (transactions) {
        if (this.filters.positions.length === 0) return transactions

        return transactions.filter(transaction => {
          let positionId = (transaction.current_position_among_many || {}).position_id

          return _.intersection([positionId], this.filters.positions).length > 0
        })
      },
      customFilterCompanies (transactions) {
        if (this.filters.companies.length === 0) return transactions

        return transactions.filter(transaction => {
          let companyUid = (transaction.company_employees[0] || {}).company_uid

          return _.intersection([companyUid], this.filters.companies).length > 0
        })
      },
      customFilterCostCenters (employeesList) {
        if (this.filters.costCenters.length === 0) return employeesList

        return employeesList.filter(employee => {
          return employee.company_employees.some(ce => {
            if (ce.latest_cost_center) {
              let costCenterId = ce.latest_cost_center.cost_center_id

              return _.intersection([costCenterId], this.filters.costCenters).length > 0
            }
          })
        })
      },
      customFilterDepartments (employeesList) {
        if (this.filters.departments.length === 0) return employeesList

        return employeesList.filter(employee => {
          return _.intersection([employee.department_id], this.filters.departments).length > 0
        })
      },
      setProgressing (status) {
        this.progressing = status
      },
    },
  }
</script>

<style scoped>
.label-position >>> .v-label.v-label--active{
  margin-left:-28px;
  background:white;
  padding-right:5px
}
</style>
